/**
 * @file index.tsx
 *
 * @fileoverview Shows applicants recommendations.
 */
import { FormattedMessage } from 'react-intl';
import { ApplicantItem } from '~/jobiqo/components/ApplicantRecommender/models';
import { ResumeTeaser } from '~/jobiqo/components/Resume/Teaser';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { ResumeTeaserPlaceholder } from '~/vcl/organisms/05-placeholders/04-resume/02-teaser';
import { Text } from '~/vcl/atoms/13-text/03-text';

// The default applicant items per page.
export const APPLICANT_RECOMMENDER_APPLICANTS_PER_PAGE = 6;

export interface ApplicantRecommenderLayoutProps {
  /**
   * The recommended applicants.
   */
  applicants: ApplicantItem[];
  /**
   * The title.
   *
   */
  Title?: React.ReactNode;
  /**
   * The description.
   *
   */
  Description?: React.ReactNode;
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The error flag.
   */
  error: boolean;
  /**
   * The number of applicants per page.
   *
   * @default APPLICANT_RECOMMENDER_APPLICANTS_PER_PAGE
   */
  applicantsPerPage?: number;
  /**
   * The flag if we want to show matching score.
   *
   * @default false
   */
  showMatchingScore?: boolean;
}

export default function ApplicantRecommenderLayout({
  Title = (
    <FormattedMessage
      id="applicant_recommender_default_title"
      defaultMessage="Similar Candidates"
    />
  ),
  Description = (
    <FormattedMessage
      id="applicant__recommender_default_description"
      defaultMessage="Meanwhile, you can browse through similar candidates - maybe the fitting candidate is already waiting for you."
    />
  ),
  applicants,
  error,
  loading,
  applicantsPerPage = APPLICANT_RECOMMENDER_APPLICANTS_PER_PAGE,
  showMatchingScore = false,
}: ApplicantRecommenderLayoutProps) {
  // Do not show recommendations if we get an error
  // or we don't have any similar applicants.
  if (error || applicants?.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <>
        <Grid columns={[1, 2]}>
          {Array.apply(null, Array(applicantsPerPage)).map(
            (_, index: number) => (
              <Cell key={index}>
                <ResumeTeaserPlaceholder />
              </Cell>
            ),
          )}
        </Grid>
      </>
    );
  }

  return (
    <Spacing paddingTop={6}>
      <h2>{Title}</h2>
      <Text color="gray7" fontSize={2}>
        {Description}
      </Text>
      <Spacing size="medium" />
      <FlexItems gap={4}>
        {applicants.map(({ resume, matchingScore }: ApplicantItem) => (
          <FlexItem key={resume.id} flexGrow={1}>
            <ResumeTeaser
              resume={resume}
              {...(showMatchingScore && { matchingScore: matchingScore })}
            />
          </FlexItem>
        ))}
      </FlexItems>
    </Spacing>
  );
}
